import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Globe,
  Loader2,
  MapPin,
  Phone,
  Building2,
  Instagram,
  Facebook,
  Music2,
  CheckCircle2,
  XCircle,
  Clock,
  Circle,
  ExternalLink,
  Utensils,
  CalendarClock,
  EyeOff,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useConfirm } from "@/contexts/ConfirmContext";
import { useGlobalToast } from "@/contexts/ToastContext";

const LANDING_BASE_URL =
  import.meta.env.VITE_LANDING_URL || "https://reservamanager.com";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ProfileStatus = "draft" | "pending_review" | "published" | "rejected";

interface PublicProfileImage {
  id: string;
  url: string;
  alt: string | null;
  sortOrder: number;
}

interface PublicProfileDetailData {
  id: string;
  status: ProfileStatus;
  description: string;
  instagramUrl: string | null;
  facebookUrl: string | null;
  tiktokUrl: string | null;
  rejectionReason: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  publishedAt: string | null;
  images: PublicProfileImage[];
  publicUrl?: string;
  location: {
    id: string;
    name: string;
    slug: string;
    city: string;
    phone: string | null;
    logo: string | null;
    coverImage: string | null;
  };
  organization: {
    name: string;
    slug: string;
    bookingMode: "tables" | "appointments";
  };
}

const STATUS_CONFIG: Record<
  ProfileStatus,
  { label: string; badgeClass: string; icon: typeof Circle }
> = {
  draft: { label: "Borrador", badgeClass: "bg-gray-100 text-gray-700", icon: Circle },
  pending_review: {
    label: "Pendiente de revisión",
    badgeClass: "bg-yellow-100 text-yellow-800",
    icon: Clock,
  },
  published: {
    label: "Publicada",
    badgeClass: "bg-green-100 text-green-800",
    icon: CheckCircle2,
  },
  rejected: {
    label: "Rechazada",
    badgeClass: "bg-red-100 text-red-800",
    icon: XCircle,
  },
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string } } };
  return e.response?.data?.message || fallback;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminPublicProfileDetail() {
  const { id } = useParams<{ id: string }>();
  const { confirm } = useConfirm();
  const { showToast } = useGlobalToast();

  const [profile, setProfile] = useState<PublicProfileDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [showRejectBox, setShowRejectBox] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showUnpublishBox, setShowUnpublishBox] = useState(false);
  const [unpublishReason, setUnpublishReason] = useState("");

  // Vista previa: token temporal (15 min) que desbloquea la ficha real aunque
  // esté en borrador/revisión/rechazada — la URL pública directa da 404 para
  // todo lo no publicado (ver Documentos/PLAN_PERFIL_PUBLICO.md Fase 2).
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/admin/public-profiles/${id}`);
      setProfile(res.data.data);
    } catch (err) {
      showToast(getErrorMessage(err, "Error al cargar la página web"), "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPreview = useCallback(async () => {
    if (!id) return;
    setPreviewLoading(true);
    setPreviewError(false);
    try {
      const res = await apiClient.post(
        `/admin/public-profiles/${id}/preview-token`,
      );
      setPreviewUrl(res.data.data.url);
    } catch {
      // No rompemos la pantalla: los datos y fotos de la izquierda siguen
      // permitiendo decidir sin la vista previa.
      setPreviewUrl(null);
      setPreviewError(true);
    } finally {
      setPreviewLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    loadPreview();
  }, [load, loadPreview]);

  const handleApprove = async () => {
    if (!id) return;
    const ok = await confirm({
      title: "¿Aprobar y publicar esta página?",
      message:
        "La página quedará visible públicamente de inmediato. El cliente podrá seguir editándola sin que vuelva a pasar por revisión.",
      confirmText: "Sí, aprobar",
    });
    if (!ok) return;

    setActionLoading(true);
    try {
      await apiClient.post(`/admin/public-profiles/${id}/approve`);
      await load();
      showToast("Página aprobada y publicada", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Error al aprobar la página"), "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!id || rejectReason.trim().length < 10) return;
    setActionLoading(true);
    try {
      await apiClient.post(`/admin/public-profiles/${id}/reject`, {
        reason: rejectReason.trim(),
      });
      setShowRejectBox(false);
      setRejectReason("");
      await load();
      showToast("Página rechazada", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Error al rechazar la página"), "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnpublish = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await apiClient.post(`/admin/public-profiles/${id}/unpublish`, {
        reason: unpublishReason.trim() || undefined,
      });
      setShowUnpublishBox(false);
      setUnpublishReason("");
      await load();
      showToast("Página despublicada", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Error al despublicar la página"), "error");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 md:p-6">
        <div className="max-w-2xl mx-auto text-center text-gray-500 py-12">
          No se ha encontrado esta página web.
        </div>
      </div>
    );
  }

  const cfg = STATUS_CONFIG[profile.status];
  const StatusIcon = cfg.icon;
  const publicUrl =
    profile.publicUrl || `${LANDING_BASE_URL}/r/${profile.location.slug}`;
  const sortedImages = [...profile.images].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <Link
          to="/admin/public-profiles"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a páginas web
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 md:mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Globe className="w-6 h-6 text-primary-600" />
              {profile.location.name}
            </h1>
            <p className="text-gray-500 text-sm mt-1 flex items-center gap-1.5 flex-wrap">
              <MapPin className="w-3.5 h-3.5" />
              {profile.location.city} · {profile.organization.name}
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ml-1 ${cfg.badgeClass}`}
              >
                <StatusIcon className="w-3 h-3" />
                {cfg.label}
              </span>
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {profile.status === "pending_review" && (
              <>
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  Aprobar
                </button>
                <button
                  onClick={() => setShowRejectBox((v) => !v)}
                  disabled={actionLoading}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  <XCircle className="w-4 h-4" />
                  Rechazar
                </button>
              </>
            )}
            {profile.status === "published" && (
              <button
                onClick={() => setShowUnpublishBox((v) => !v)}
                disabled={actionLoading}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                <EyeOff className="w-4 h-4" />
                Despublicar
              </button>
            )}
          </div>
        </div>

        {/* Reject box */}
        {showRejectBox && (
          <div className="mb-4 p-4 bg-red-50 rounded-xl border border-red-100">
            <p className="text-sm font-medium text-red-800 mb-2">
              Motivo del rechazo (obligatorio, mínimo 10 caracteres)
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Explica al cliente qué debe corregir antes de volver a solicitar la publicación..."
              className="w-full px-3 py-2 text-sm border border-red-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <span
                className={`text-xs ${
                  rejectReason.trim().length < 10
                    ? "text-red-400"
                    : "text-red-600"
                }`}
              >
                {rejectReason.trim().length}/10 caracteres mínimo
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setShowRejectBox(false);
                    setRejectReason("");
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleReject}
                  disabled={actionLoading || rejectReason.trim().length < 10}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {actionLoading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5" />
                  )}
                  Confirmar rechazo
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unpublish box */}
        {showUnpublishBox && (
          <div className="mb-4 p-4 bg-gray-100 rounded-xl border border-gray-200">
            <p className="text-sm font-medium text-gray-800 mb-2">
              Motivo de la despublicación (opcional)
            </p>
            <textarea
              value={unpublishReason}
              onChange={(e) => setUnpublishReason(e.target.value)}
              rows={2}
              placeholder="Motivo interno, no se muestra al cliente en la página..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400 resize-none"
            />
            <div className="flex items-center justify-end gap-2 mt-2">
              <button
                onClick={() => {
                  setShowUnpublishBox(false);
                  setUnpublishReason("");
                }}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={handleUnpublish}
                disabled={actionLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-gray-700 rounded-lg hover:bg-gray-800 disabled:opacity-50"
              >
                {actionLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <EyeOff className="w-3.5 h-3.5" />
                )}
                Confirmar despublicación
              </button>
            </div>
          </div>
        )}

        {/* Rejection reason (histórico) */}
        {profile.status === "rejected" && profile.rejectionReason && (
          <div className="mb-4 p-4 bg-red-50 rounded-xl border border-red-100">
            <p className="text-sm text-red-800">
              <strong>Motivo del rechazo:</strong> {profile.rejectionReason}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Datos y fotos */}
          <div className="space-y-4 md:space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-400" />
                Negocio
              </h2>
              <dl className="text-sm space-y-2">
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">Local</dt>
                  <dd className="text-gray-900 font-medium text-right">
                    {profile.location.name}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">Organización</dt>
                  <dd className="text-gray-900 text-right">
                    {profile.organization.name}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">Ciudad</dt>
                  <dd className="text-gray-900 text-right">
                    {profile.location.city}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500 flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />
                    Teléfono
                  </dt>
                  <dd className="text-gray-900 text-right">
                    {profile.location.phone || "—"}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">Modalidad</dt>
                  <dd className="text-gray-900 text-right inline-flex items-center gap-1">
                    {profile.organization.bookingMode === "appointments" ? (
                      <CalendarClock className="w-3.5 h-3.5" />
                    ) : (
                      <Utensils className="w-3.5 h-3.5" />
                    )}
                    {profile.organization.bookingMode === "appointments"
                      ? "Citas"
                      : "Mesas"}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">Solicitado</dt>
                  <dd className="text-gray-900 text-right">
                    {formatDate(profile.submittedAt)}
                  </dd>
                </div>
                {profile.reviewedAt && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-500">Revisado</dt>
                    <dd className="text-gray-900 text-right">
                      {formatDate(profile.reviewedAt)}
                    </dd>
                  </div>
                )}
              </dl>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary-700 hover:text-primary-800 break-all"
              >
                <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                {publicUrl}
              </a>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3">
                Descripción
              </h2>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {profile.description || (
                  <span className="text-gray-400 italic">Sin descripción</span>
                )}
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3">
                Redes sociales
              </h2>
              <div className="space-y-2 text-sm">
                <SocialLink
                  icon={Instagram}
                  url={profile.instagramUrl}
                  label="Instagram"
                />
                <SocialLink
                  icon={Facebook}
                  url={profile.facebookUrl}
                  label="Facebook"
                />
                <SocialLink
                  icon={Music2}
                  url={profile.tiktokUrl}
                  label="TikTok"
                />
                {!profile.instagramUrl &&
                  !profile.facebookUrl &&
                  !profile.tiktokUrl && (
                    <p className="text-gray-400 italic">
                      Sin redes sociales configuradas
                    </p>
                  )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3">
                Fotos ({sortedImages.length})
              </h2>
              {sortedImages.length === 0 ? (
                <p className="text-sm text-gray-400 italic">Sin fotos</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {sortedImages.map((img) => (
                    <div
                      key={img.id}
                      className="aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100"
                    >
                      <img
                        src={img.url}
                        alt={img.alt || ""}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Vista previa */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6 flex flex-col">
            <div className="flex items-center justify-between mb-3 gap-2">
              <h2 className="text-base font-semibold text-gray-900">
                Vista previa
              </h2>
              <div className="flex items-center gap-3">
                {previewUrl && (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary-700 hover:text-primary-800"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Abrir en pestaña nueva
                  </a>
                )}
                <button
                  onClick={loadPreview}
                  disabled={previewLoading}
                  title="Recargar vista previa"
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  <RefreshCw
                    className={`w-3.5 h-3.5 ${previewLoading ? "animate-spin" : ""}`}
                  />
                  Recargar vista previa
                </button>
              </div>
            </div>

            {previewUrl ? (
              <div className="flex-1 min-h-[70vh] rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                <iframe
                  src={previewUrl}
                  title="Vista previa de la página pública"
                  className="w-full h-full min-h-[70vh]"
                />
              </div>
            ) : (
              <div className="flex-1 min-h-[70vh] rounded-lg border border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center text-center p-6">
                {previewLoading ? (
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin mb-2" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-amber-500 mb-2" />
                )}
                <p className="text-sm text-gray-500">
                  {previewLoading
                    ? "Generando vista previa..."
                    : "La vista previa no está disponible en este momento."}
                </p>
                {!previewLoading && previewError && (
                  <button
                    onClick={loadPreview}
                    className="mt-2 text-xs font-medium text-primary-700 hover:text-primary-800 underline"
                  >
                    Reintentar
                  </button>
                )}
                {!previewLoading && (
                  <p className="text-xs text-gray-400 mt-2">
                    Los datos y las fotos de la izquierda siguen disponibles
                    para decidir.
                  </p>
                )}
              </div>
            )}

            <p className="text-xs text-gray-400 mt-2">
              {previewUrl
                ? "El enlace de vista previa caduca a los 15 minutos. Usa \"Recargar vista previa\" si llevas un rato con la pestaña abierta."
                : "Si necesitas reintentarlo, usa el botón de arriba."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SocialLink({
  icon: Icon,
  url,
  label,
}: {
  icon: typeof Instagram;
  url: string | null;
  label: string;
}) {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 text-primary-700 hover:text-primary-800 break-all"
    >
      <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <span className="text-gray-500">{label}:</span> {url}
    </a>
  );
}
